import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

// Services
import { RouteParam, ServiceBase } from '../service-base';

// Modèles
import { AgentAction } from 'src/app/@shared/models/ai/agent-action.model';

// Utils
import { Filter } from 'src/app/@shared/utilities/models/filter';

const CLASS_NAME = 'AgentAction';
const ENDPOINT = '/agent/actions/';
const ROUTES: RouteParam[] = [
    { key: 'apply', path: '{id}/apply/' },
    { key: 'reject', path: '{id}/reject/' },
];

/**
 * Journal des écritures de l'agent IA (#1217) dans le dossier courant : mes actions, celles
 * que j'ai tranchées et les propositions en attente que j'ai le droit d'appliquer.
 *
 * Les actions naissent dans le chat (event SSE `action`), jamais par ce service. Il sert
 * à relire une carte après un geste, à lister les propositions en attente, et à appliquer
 * ou refuser une proposition — un refus métier s'affiche comme partout, et la proposition
 * reste en attente.
 */
@Injectable({
    providedIn: 'root'
})
export class AgentActionService extends ServiceBase {

    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addRoutes(ROUTES);
    }

    /**
     * Appliquer une proposition : le serveur rejoue la vraie vue au nom de l'utilisateur.
     * La valeur renvoyée (l'objet écrit, ou `null`) est ignorée : l'appelant relit la carte.
     */
    public apply$(id: number): Observable<void> {
        return this._putActionValue$(this.routes.apply.replace('{id}', id.toString())).pipe(
            map(() => undefined)
        );
    }

    /** Les propositions en attente que je vois : les miennes, et celles que je peux appliquer. */
    public listProposed$(): Observable<AgentAction[]> {
        return this.getList$({
            filters: [new Filter({ property: 'processingStatus', value: 'proposed' })],
            activityIndicator: false
        });
    }

    /** Relit une action. La route n'a pas de lecture unitaire : la liste, filtrée sur l'identifiant. */
    public read$(id: number): Observable<AgentAction | null> {
        return this.getList$({
            filters: [new Filter({ property: 'id', value: id })],
            activityIndicator: false
        }).pipe(
            map((actions: AgentAction[]) => actions?.[0] ?? null)
        );
    }

    /** Refuser une proposition : son auteur la retire, ou qui pourrait l'appliquer l'écarte. */
    public reject$(id: number): Observable<void> {
        return this._putActionValue$(this.routes.reject.replace('{id}', id.toString())).pipe(
            map(() => undefined)
        );
    }
}
